//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import QpRangeModel from './qpRange.model';

/**
 * @service QpRangeService
 * @description Service for managing Quality Parameter (QP) Ranges within the aquaculture system.
 * QP Ranges define the specific thresholds (minimum and maximum) for various water quality parameters
 * (such as pH, Dissolved Oxygen, Temperature, Salinity) tailored to specific contexts.
 *
 * These contexts are typically defined by:
 * 1. **Culture Category**: The stage of the culture (e.g., Nursery, Grow-out).
 * 2. **Species Type**: The species being cultured (e.g., L. Vannamei, P. Monodon).
 *
 * The ranges managed here (Acceptable, Desirable, Stress) are critical for:
 * - **Validation**: Checking if entered readings are plausible.
 * - **Alerting**: Triggering warnings or critical alerts when readings deviate from optimal conditions.
 * - **Reporting**: Color-coding reports to highlight problem areas.
 *
 * This service provides the interface to retrieve these definitions from the backend API.
 *
 * @feature 1. Retrieve all defined Quality Parameter Ranges.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all configuration data is loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<QpRangeModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/parameterranges/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class QpRangeService extends Service<QpRangeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/parameterranges/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/parameterranges/');
  }

  /**
   * @method getAllQpRanges
   * @async
   * @description Fetch all Quality Parameter Range records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all range configurations are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<QpRangeModel>>} Promise resolving to an array of QpRangeModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * qpRangeService.getAllQpRanges().then(ranges => {
   *   console.log('QP Ranges:', ranges);
   * });
   */
  public getAllQpRanges() {
    return new Promise<Array<QpRangeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<QpRangeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
